import { useMemo, useState, type FormEvent } from "react";

import { Button, Checkbox, Modal } from "@/components/common";
import { useCreateRole, usePermissions, useUpdateRole } from "@/hooks/useRoles";
import { extractErrorMessage } from "@/services/axiosConfig";
import type { Permission, Role } from "@/types/role";

const RESOURCE_LABEL: Record<string, string> = {
  workspaces: "Workspace",
  organizations: "Organizações",
  clients: "Empresas",
  contracts: "Contratos",
  teses: "Teses",
  pii: "Dados sensíveis (PII)",
  marketing: "Marketing",
  audit: "Auditoria",
  access_grants: "Membros",
  roles: "Papéis",
  users: "Convites de usuário",
  lgpd: "LGPD",
};

function groupByResource(perms: Permission[]): [string, Permission[]][] {
  const map = new Map<string, Permission[]>();
  for (const p of perms) {
    const list = map.get(p.resource) ?? [];
    list.push(p);
    map.set(p.resource, list);
  }
  return [...map.entries()];
}

type Props = {
  workspaceId: number;
  role: Role | null; // null = criar; com role = editar (ou ver, se is_system)
  onClose: () => void;
};

export function RoleFormModal({ workspaceId, role, onClose }: Props) {
  const { data: permissions = [] } = usePermissions(workspaceId);
  const create = useCreateRole(workspaceId);
  const update = useUpdateRole(workspaceId);

  const readOnly = role?.is_system ?? false;
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(new Set(role?.permission_codes ?? []));
  const [error, setError] = useState<string | null>(null);

  // Para papel de sistema, mostra só as permissões que ele tem (catálogo + as dele).
  const visiblePerms = useMemo(() => {
    if (!readOnly) return permissions;
    return permissions.filter((p) => selected.has(p.code));
  }, [permissions, readOnly, selected]);

  const groups = useMemo(() => groupByResource(visiblePerms), [visiblePerms]);
  const busy = create.isPending || update.isPending;

  function toggle(code: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (readOnly) return;
    setError(null);
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      permission_codes: [...selected],
    };
    try {
      if (role) await update.mutateAsync({ roleId: role.id, payload });
      else await create.mutateAsync(payload);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  const title = role ? (readOnly ? `Papel: ${role.name}` : "Editar papel") : "Novo papel";

  return (
    <Modal
      onClose={onClose}
      title={title}
      subtitle={readOnly ? "Papel de sistema — somente leitura." : undefined}
      size="lg"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            {readOnly ? "Fechar" : "Cancelar"}
          </Button>
          {!readOnly && (
            <Button
              variant="primary"
              type="submit"
              form="role-form"
              disabled={busy || !name.trim()}
            >
              {busy ? "Salvando…" : "Salvar"}
            </Button>
          )}
        </>
      }
    >
      <form
        id="role-form"
        onSubmit={onSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {error && <div className="form-error">{error}</div>}

        <label className="form-field">
          <span>Nome</span>
          <input
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={readOnly}
            required
            autoFocus={!readOnly}
          />
        </label>
        <label className="form-field">
          <span>Descrição</span>
          <input
            className="form-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={readOnly}
          />
        </label>

        <div className="form-field">
          <span>Permissões</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 4 }}>
            {groups.map(([resource, perms]) => (
              <fieldset key={resource} className="invite-checklist">
                <legend>{RESOURCE_LABEL[resource] ?? resource}</legend>
                {perms.map((p) => (
                  <Checkbox
                    key={p.code}
                    checked={selected.has(p.code)}
                    onChange={() => toggle(p.code)}
                    disabled={readOnly}
                    label={p.description}
                  />
                ))}
              </fieldset>
            ))}
            {groups.length === 0 && <p className="td-small">Nenhuma permissão disponível.</p>}
          </div>
        </div>
      </form>
    </Modal>
  );
}
